import {
  Form,
  Loading,
  SaveButton,
  useGetOne,
  useInput,
  useRedirect,
  useTranslate
} from 'react-admin'
import { useFormState, useFormContext } from 'react-hook-form'
import React, { useState } from 'react'
import {
  Box,
  Grid,
  Input,
  Typography,
  useMediaQuery,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl
} from '@mui/material'
import log from 'loglevel'
import { isEmpty, isNil, map, split } from 'lodash/fp'
import axios from 'axios'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { format } from 'date-fns/fp'

import { CallStatus } from '../../types'
import { ScheduleButton } from '../PartnerSchedule'
import { HeaderLogo, setTitle } from '../Util'

log.setLevel(log.levels.DEBUG)

type SelectItem = {
  label: string
  value: string
}

const env = process.env['REACT_APP_ENV']
const urls = {
  prd: 'https://api-b2b-v2.terashite.jp/partner-user-public/v1/partnerReport',
  stg: 'https://api-b2b-v2-stg.terashite.jp/partner-user-public/v1/partnerReport',
  int: 'https://api-b2b-v2-int.dev.terashite.jp/partner-user-public/v1/partnerReport',
  dev: 'https://api-b2b-v2.dev.terashite.jp/partner-user-public/v1/partnerReport',
  exp: 'https://api-b2b-v2-exp.dev.terashite.jp/partner-user-public/v1/partnerReport'
}
const url = urls[env as keyof typeof urls]
const schemaCommon = {
  status: z.string().min(1, { message: 'required' }),
  report: z.union([z.string(), z.null(), z.undefined()])
}
const schema = z.object(schemaCommon)

const SubmitButton: React.FC<{
  id: string
  bankAccount?: boolean
}> = ({ id, ...props }) => {
  const [enable, setEnable] = useState(false)
  const { getValues } = useFormContext()
  const { errors, isValid } = useFormState()
  const translate = useTranslate()
  const redirect = useRedirect()
  const onSubmit = (): void => {
    const { ...data } = getValues()
    setEnable(false)
    const input = {
      id,
      date: format('yyyy-MM-dd HH:mm')(Date.now()),
      ...data
    }
    log.debug('[onSubmit]', { input })
    axios({
      url,
      method: 'POST',
      data: input
    })
      .then(() => {
        redirect(`/partnerReport/${id}/complete`)
      })
      .catch(log.error)
  }
  React.useEffect(() => {
    log.debug('[SubmitButton]', { errors, isValid, data: getValues() })
    setEnable(isValid)
  }, [errors, isValid, getValues])
  return (
    <SaveButton
      {...props}
      icon={<></>}
      variant="contained"
      size="large"
      disabled={!enable}
      sx={{
        borderRadius: '40px',
        backgroundColor: '#4472c4'
      }}
      label={translate('resources.partnerReport.submit.l1')}
      onClick={onSubmit}
    />
  )
}
const FormContent: React.FC<{
  id: string
  user: string
  bankAccount?: boolean
}> = ({ id, user, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const { data, isLoading } = useGetOne('users', { id: user })
  if (isLoading) return <Loading />
  const { place, placeNumber, schedules, partner } = data
  const customHeader = translate(
    `resources.partnerReport.${partner as string}.header`
  )
  const defaultHeader = translate(`resources.partnerReport.header`)
  const header =
    customHeader === `resources.partnerReport.${partner as string}.header`
      ? defaultHeader
      : customHeader
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '1280px' }}>
        <Box sx={{ marginLeft: '0.5em' }}>
          <HeaderLogo partner={partner} />
        </Box>
        <Box sx={{ backgroundColor: '#fff7e1' }}>
          <Box
            sx={{
              marginLeft: isDesktop ? '2em' : '0.5em',
              marginRight: '0.5em'
            }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em'
              }}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography
                  variant={isDesktop ? 'h4' : 'h6'}
                  sx={{
                    marginTop: '1.5em'
                  }}>
                  {translate('resources.partnerReport.title')}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '-1em'
                }}>
                <picture>
                  <img src="/bar.png" alt="bar" />
                </picture>
              </Grid>
              <Grid item xs={12}>
                {map((text: string) =>
                  text === '' ? (
                    <p />
                  ) : (
                    <Typography variant={isDesktop ? 'h6' : 'subtitle1'}>
                      {text}
                    </Typography>
                  )
                )(split('\n')(header))}
                <p />
              </Grid>
              {/* <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 6 : 12}>
                    <TextField source="name" value={name} />
                  </Grid>
                </Grid>
              </Grid> */}
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 6 : 12}>
                    <TextField source="place" value={place} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={isDesktop ? 6 : 12}>
                    <TextField source="placeNumber" value={placeNumber} />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12}>
                    <SelectInput
                      source="status"
                      choices={[
                        { label: '未確認', value: CallStatus.yet },
                        { label: '確認完了', value: CallStatus.confirmed }
                      ]}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12}>
                    <TextInput source="report" placeholder="詳細等" multiline />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {map((text: string) =>
                  text === '' ? (
                    <p />
                  ) : (
                    <Typography variant={isDesktop ? 'h6' : 'subtitle1'}>
                      {text}
                    </Typography>
                  )
                )(split('\n')(translate('resources.partnerReport.schedule')))}
                <p />
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12}>
                    <ScheduleButton
                      source="schedules"
                      label="resources.partnerReport.fields.schedules"
                      schedules={
                        isNil(schedules) || isEmpty(schedules)
                          ? [format('yyyy-MM-dd')(Date.now())]
                          : schedules
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '1.5em',
                  marginBottom: '2.5em'
                }}>
                <SubmitButton {...props} id={id} />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const TextInput: React.FC<{
  source: string
  placeholder: string
  validate?: any
  multiline?: boolean
}> = ({ source, placeholder, validate, multiline, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { field, isRequired } = useInput({ source, validate, ...props })
  const translate = useTranslate()
  const label = translate(`resources.partnerReport.fields.${field.name}`)
  // const { errors } = useFormState()
  const multilineValue = isNil(multiline) ? false : multiline
  return (
    <>
      <Typography variant="subtitle1">{label}</Typography>
      <Box
        {...props}
        border={1}
        sx={{
          minHeight: '3em',
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em',
          marginLeft: isDesktop ? '0.50em' : '0.25em',
          borderRadius: '10px',
          borderColor: '#c5c5c5'
        }}>
        <Input
          {...field}
          placeholder={placeholder}
          fullWidth
          disableUnderline
          minRows={3}
          multiline={multilineValue}
          sx={{ marginLeft: '1em', marginRight: '1em', fontSize: '20px' }}
          required={isRequired}
        />
      </Box>
      {/* {!isNil(errors[source]?.message) && <ValidateError source={source} />} */}
    </>
  )
}
export const TextField: React.FC<{
  source: string
  value: string
}> = ({ source, value, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const translate = useTranslate()
  const label = translate(`resources.partnerReport.fields.${source}`)
  return (
    <>
      <Typography variant="subtitle1">{label}</Typography>
      <Box
        {...props}
        sx={{
          height: '3em',
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em',
          marginLeft: isDesktop ? '0.50em' : '0.25em'
        }}>
        <Typography variant="h6">{value}</Typography>
      </Box>
    </>
  )
}
export const SelectInput: React.FC<{
  source: string
  choices: SelectItem[]
}> = ({ source, choices, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { field, isRequired } = useInput({ source, ...props })
  const translate = useTranslate()
  const label = translate(`resources.partnerReport.fields.${field.name}`)
  const { errors } = useFormState()
  return (
    <>
      <Typography variant="subtitle1">{label}</Typography>
      <Box
        {...props}
        sx={{
          minHeight: '3em',
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em',
          marginLeft: isDesktop ? '0.50em' : '0.25em'
        }}>
        <FormControl required={isRequired} fullWidth>
          <RadioGroup
            {...field}
            row={isDesktop}
            sx={{ marginLeft: '1em', marginRight: '1em', fontSize: '20px' }}>
            {map((item: SelectItem) => (
              <FormControlLabel
                value={item.value}
                control={<Radio />}
                label={<Typography variant="h5">{item.label}</Typography>}
              />
            ))(choices)}
          </RadioGroup>
        </FormControl>
      </Box>
      {!isNil(errors[source]?.message) && <ValidateError source={source} />}
    </>
  )
}
export const ValidateError: React.FC<{
  source: string
}> = ({ source }) => {
  const translate = useTranslate()
  const { errors } = useFormState()
  return (
    <Typography variant="subtitle2" color="red" sx={{ marginLeft: '1em' }}>
      {translate(errors[source]?.message as string)}
    </Typography>
  )
}
export const PartnerReportForm: React.FC<{
  id: string
}> = ({ id, ...props }) => {
  const translate = useTranslate()
  const { data, isLoading } = useGetOne('notifications', { id })
  React.useEffect(() => {
    if (isLoading) return
    const { partner } = data
    setTitle(partner, translate)
  }, [isLoading, data, translate])
  if (isLoading) return <Loading />
  const { user } = data
  return (
    <Form resolver={zodResolver(schema)} mode="onBlur" reValidateMode="onBlur">
      <FormContent {...props} id={id} user={user} />
    </Form>
  )
}
export const PartnerReportCompleteForm: React.FC<{ id: string }> = ({ id }) => {
  const translate = useTranslate()
  const { data, isLoading } = useGetOne('notifications', { id })
  React.useEffect(() => {
    if (isLoading) return
    const { partner } = data
    setTitle(partner, translate)
  }, [isLoading, data, translate])
  if (isLoading) return <Loading />
  const { partner } = data
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '1280px' }}>
        <Box sx={{ marginLeft: '0.5em' }}>
          <HeaderLogo partner={partner} />
        </Box>
        <Box
          sx={{
            backgroundColor: '#fff7e1',
            paddingTop: '2em',
            paddingBottom: '3em'
          }}>
          <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
            <Grid
              container
              justifyContent="center"
              spacing={2}
              sx={{
                marginTop: '0.1em'
              }}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography
                  variant="h6"
                  sx={{
                    marginTop: '0em'
                  }}>
                  {translate('resources.partnerReport.response')}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
